import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./AdminPanelsDashboards.css";

export default function AdminPanelsDashboards() {
    const dashboardCards = [
        { title: "Player Management", value: "148K", label: "active player profiles", icon: "fa-solid fa-users" },
        { title: "Game Rooms", value: "3.8K", label: "live tables and matches", icon: "fa-solid fa-gamepad" },
        { title: "Wallet Controls", value: "Secure", label: "coins, cash, refunds, rewards", icon: "fa-solid fa-wallet" },
        { title: "LiveOps Events", value: "Live", label: "offers, missions, bonuses", icon: "fa-solid fa-bolt" }
    ];

    const modules = [
        "Player Accounts",
        "KYC & User Status",
        "Rooms & Tables",
        "Tournament Manager",
        "Leaderboard Engine",
        "Event Campaigns",
        "Wallet & Rewards",
        "Fraud Review",
        "Game Reports",
        "Support Actions",
        "Push Notifications",
        "Role Permissions"
    ];

    const tournaments = [
        { name: "Weekend Rummy League", type: "Knockout", players: "2,480", prize: "Coins + Cash", status: "Live" },
        { name: "Ludo Speed Cup", type: "Timed", players: "1,260", prize: "Reward Pool", status: "Scheduled" },
        { name: "Poker Pro Series", type: "Multi-table", players: "840", prize: "Cash Table", status: "Review" },
        { name: "Fantasy Mega Contest", type: "Contest", players: "7,900", prize: "Rank Prizes", status: "Live" }
    ];

    const leaders = [
        { rank: "#01", player: "AceKing_92", score: "98,420", game: "Rummy", reward: "Gold Chest" },
        { rank: "#02", player: "LudoMaster", score: "94,880", game: "Ludo", reward: "Coins Pack" },
        { rank: "#03", player: "CardBoss", score: "91,500", game: "Poker", reward: "VIP Badge" },
        { rank: "#04", player: "QuizRush", score: "88,230", game: "Quiz", reward: "Bonus Spin" }
    ];

    const events = [
        { event: "Daily Login Streak", segment: "All Players", trigger: "Login", metric: "Retention" },
        { event: "Win Back Bonus", segment: "Inactive 7 Days", trigger: "Push + Offer", metric: "Reactivation" },
        { event: "Festival Tournament", segment: "High Value", trigger: "Entry Fee", metric: "Revenue" },
        { event: "Referral Race", segment: "New Users", trigger: "Invite", metric: "Acquisition" }
    ];

    const process = [
        { step: "01", title: "Admin Scope", text: "Define games, user roles, operations flows, reports, wallet actions, tournaments, and support permissions." },
        { step: "02", title: "Dashboard UX", text: "Design table-heavy workflows, filters, bulk actions, statuses, alerts, charts, and review states." },
        { step: "03", title: "Secure Build", text: "Build frontend, APIs, RBAC permissions, audit logs, validation, and admin-safe actions." },
        { step: "04", title: "Game Integration", text: "Connect game servers, databases, wallet systems, analytics, notifications, and LiveOps services." },
        { step: "05", title: "Testing & Launch", text: "Test permissions, data accuracy, performance, responsiveness, edge cases, and deployment readiness." }
    ];

    const faqs = [
        {
            question: "What gaming admin panels can Oengines build?",
            answer: "We build admin panels for player management, game rooms, tournaments, leaderboards, wallets, rewards, LiveOps events, analytics, reports, fraud review, and support workflows."
        },
        {
            question: "Can dashboards manage tournaments and leaderboards?",
            answer: "Yes. We can build tournament setup, contest rules, player entries, prize configuration, rank logic, leaderboard views, and reward distribution workflows."
        },
        {
            question: "Can admin panels connect with existing game backend systems?",
            answer: "Yes. We can integrate APIs, databases, realtime game servers, wallet systems, analytics providers, notifications, and cloud infrastructure."
        },
        {
            question: "Will the admin panel be secure?",
            answer: "Yes. We plan role-based access, protected actions, audit logs, validation, permission checks, secure APIs, and admin workflow safeguards."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Gaming Admin Panel and Dashboard Development Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Admin Panel and Dashboard Development",
        "areaServed": "Worldwide",
        "description": "Gaming admin panel and dashboard development for tournaments, leaderboards, LiveOps events, player management, wallet controls, reports, analytics, fraud review, and game operations."
    };

    return (
        <>
            <Helmet>
                <title>Gaming Admin Panel & Dashboard Development | Oengines</title>
                <meta name="description" content="Oengines Games LLP builds gaming admin panels and dashboards for tournaments, leaderboards, events, LiveOps, player management, wallets, analytics, reports, and game operations." />
                <meta name="keywords" content="gaming admin panel development, game dashboard development, tournament dashboard, leaderboard dashboard, LiveOps admin panel, game analytics dashboard, wallet admin panel" />
                <link rel="canonical" href="https://www.oengines.com/admin-panels-dashboards" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="gameadmin-page">
                <section className="gameadmin-hero">
                    <div className="container">
                        <div className="gameadmin-hero-grid">
                            <div>
                                <span className="gameadmin-eyebrow">Gaming Admin Panels & Dashboards</span>
                                <h1>Control Game Operations, Tournaments, Events & Player Data</h1>
                                <p>Oengines Games LLP builds secure admin panels and dashboards for game studios, real-money platforms, multiplayer games, fantasy sports apps, and LiveOps teams that need clear control over daily operations.</p>
                                <div className="gameadmin-actions">
                                    <a href="/contact" className="as-btn">Build Admin Dashboard <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="gameadmin-call">Call: (0261) - 4054978</a>
                                </div>
                            </div>
                            <div className="gameadmin-command">
                                <div className="gameadmin-command-top">
                                    <span></span><span></span><span></span>
                                    <strong>Admin Command Center</strong>
                                </div>
                                <div className="gameadmin-command-body">
                                    {dashboardCards.map((item) => (
                                        <article key={item.title}>
                                            <i className={item.icon}></i>
                                            <strong>{item.value}</strong>
                                            <span>{item.title}</span>
                                            <small>{item.label}</small>
                                        </article>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="gameadmin-module-section space">
                    <div className="container">
                        <div className="gameadmin-heading">
                            <span>Admin Modules</span>
                            <h2>Game Dashboard Modules for Daily Operations</h2>
                            <p>Build a practical backend office for the people who manage players, rooms, rewards, tournaments, reports, support, and LiveOps campaigns.</p>
                        </div>
                        <div className="gameadmin-module-grid">
                            {modules.map((item) => <span key={item}>{item}</span>)}
                        </div>
                    </div>
                </section>

                <section className="gameadmin-tournament-section space">
                    <div className="container">
                        <div className="gameadmin-split-heading">
                            <div className="gameadmin-heading mb-0">
                                <span>Tournament Manager</span>
                                <h2>Create, Track & Control Game Tournaments</h2>
                            </div>
                            <p>Manage tournament setup, entry rules, prize pools, brackets, player eligibility, status changes, settlement, and reports from a structured admin workflow.</p>
                        </div>
                        <div className="gameadmin-table-wrap">
                            <table className="gameadmin-table">
                                <thead>
                                    <tr>
                                        <th>Tournament</th>
                                        <th>Type</th>
                                        <th>Players</th>
                                        <th>Prize</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tournaments.map((item) => (
                                        <tr key={item.name}>
                                            <td>{item.name}</td>
                                            <td>{item.type}</td>
                                            <td>{item.players}</td>
                                            <td>{item.prize}</td>
                                            <td><span>{item.status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                <section className="gameadmin-leaderboard-section space">
                    <div className="container">
                        <div className="row align-items-center g-5">
                            <div className="col-lg-5">
                                <div className="gameadmin-heading">
                                    <span>Leaderboard Dashboard</span>
                                    <h2>Rank Players, Monitor Scores & Trigger Rewards</h2>
                                    <p>Leaderboards can support daily, weekly, seasonal, tournament-specific, region-wise, and game-mode ranking rules with clear reward tracking.</p>
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="gameadmin-rank-list">
                                    {leaders.map((item) => (
                                        <article key={item.rank}>
                                            <strong>{item.rank}</strong>
                                            <div>
                                                <h3>{item.player}</h3>
                                                <span>{item.game} / {item.reward}</span>
                                            </div>
                                            <em>{item.score}</em>
                                        </article>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="gameadmin-event-section space">
                    <div className="container">
                        <div className="gameadmin-split-heading">
                            <div className="gameadmin-heading mb-0">
                                <span>Events & LiveOps</span>
                                <h2>Plan Events, Offers, Missions & Retention Campaigns</h2>
                            </div>
                            <p>LiveOps dashboards help product teams create events, target segments, configure rewards, monitor campaign performance, and improve retention.</p>
                        </div>
                        <div className="gameadmin-event-grid">
                            {events.map((item) => (
                                <article key={item.event}>
                                    <span>{item.metric}</span>
                                    <h3>{item.event}</h3>
                                    <dl>
                                        <div><dt>Segment</dt><dd>{item.segment}</dd></div>
                                        <div><dt>Trigger</dt><dd>{item.trigger}</dd></div>
                                    </dl>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="gameadmin-visual-section space">
                    <div className="container">
                        <div className="gameadmin-visual-panel">
                            <div className="gameadmin-heading mb-0">
                                <span>Dashboard Screens</span>
                                <h2>Designed for Tables, Filters, Bulk Actions & Reports</h2>
                                <p>Gaming dashboards need dense but readable views for repeated work: search, filters, statuses, exports, review queues, permission-based actions, and data confidence.</p>
                            </div>
                            <div className="gameadmin-screen-collage">
                                <img src="assets/img/GamesImg/dashboard-07.png" alt="Gaming dashboard table" />
                                <img src="assets/img/GamesImg/dashboard-04.png" alt="Game admin dashboard" />
                                <img src="assets/img/GameDevelopment/react/rgcedb-1024x698.png" alt="Admin panel reports" />
                            </div>
                        </div>
                    </div>
                </section>

                <section className="gameadmin-process-section space">
                    <div className="container">
                        <div className="gameadmin-heading text-center">
                            <span>Development Process</span>
                            <h2>From Operations Requirements to Secure Admin Launch</h2>
                        </div>
                        <div className="gameadmin-process-grid">
                            {process.map((item) => (
                                <article key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="gameadmin-faq-section space">
                    <div className="container">
                        <div className="gameadmin-heading text-center">
                            <span>FAQ</span>
                            <h2>Gaming Admin Panel Questions</h2>
                        </div>
                        <div className="gameadmin-faq-grid">
                            {faqs.map((item) => (
                                <article key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="gameadmin-cta">
                    <div className="container">
                        <div className="gameadmin-cta-box">
                            <div>
                                <span>Admin Dashboard Consultation</span>
                                <h2>Need a secure dashboard for your game operations?</h2>
                                <p>Share your game type, admin roles, tournament needs, leaderboard rules, LiveOps campaigns, wallet flows, and reporting requirements.</p>
                            </div>
                            <a href="/contact" className="as-btn">Start Admin Panel Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
